import type { CSSProperties, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  CostSummary,
  Meter,
  Quota,
  UsageLine,
} from '@/shared/api/generated/metering';
import { Alert } from '@/shared/ui/Alert';
import { Card } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { useCost, useQuotas, useUsage } from '../api/metering';
import {
  hasUnpricedUsage,
  meterLabelKey,
  quotaTone,
  usageByMeter,
} from '../model/metering';

/* Usage and cost, as the design lays it out: one card for this billing period — each meter, its
 * units and its cost, with the total beneath — and one for the limits, each a bar of consumption
 * against its ceiling.
 *
 * **An unpriced meter is said in words, never charged at zero.** A blank or a zero in a money column
 * reads as "nothing owed", and a meter nobody has priced is a decision outstanding rather than a free
 * one. The bar on a limit is the scan; the figures and the standing beside it are the information. */

const UNITS = new Intl.NumberFormat();

function MeterName({ meter }: { readonly meter: Meter }): ReactElement {
  const { t } = useTranslation();
  return (
    <>
      <strong className="lq-mt__meter">{t(meterLabelKey(meter))}</strong>
      <div className="lq-mt__code">{meter}</div>
    </>
  );
}

function UsageRow({
  line,
  cost,
  currency,
}: {
  readonly line: UsageLine;
  readonly cost: string | null;
  readonly currency: string | null;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <tr>
      <td>
        <MeterName meter={line.meter} />
      </td>
      <td className="lq-table__num">{UNITS.format(line.units)}</td>
      <td className="lq-table__num">
        {cost === null ? (
          <em className="lq-mt__unpriced">{t('metering.unpriced')}</em>
        ) : (
          `${cost}${currency === null ? '' : ` ${currency}`}`
        )}
      </td>
    </tr>
  );
}

function QuotaRow({
  quota,
  consumed,
}: {
  readonly quota: Quota;
  readonly consumed: number;
}): ReactElement {
  const { t } = useTranslation();
  const tone = quotaTone(quota, consumed);
  const share =
    quota.unit_limit > 0 ? Math.min(100, (consumed / quota.unit_limit) * 100) : 100;
  const soft =
    quota.unit_limit > 0 ? Math.min(100, (quota.soft_limit / quota.unit_limit) * 100) : 0;
  return (
    <li className="lq-mt__limit">
      <div className="lq-mt__limitHead">
        <div>
          <MeterName meter={quota.meter} />
        </div>
        <div className="lq-mt__limitFigures">
          <span className="lq-mt__used" data-tone={tone}>
            {UNITS.format(consumed)}
          </span>
          {' / '}
          {UNITS.format(quota.unit_limit)}
        </div>
      </div>
      <div
        className="lq-mt__bar"
        role="meter"
        aria-valuemin={0}
        aria-valuemax={quota.unit_limit}
        aria-valuenow={Math.min(consumed, quota.unit_limit)}
        aria-label={t(meterLabelKey(quota.meter))}
        style={{ '--soft': `${String(soft)}%` } as CSSProperties}
      >
        <span
          className="lq-mt__barFill"
          data-tone={tone}
          style={{ width: `${String(share)}%` }}
        />
      </div>
      <div className="lq-mt__limitFoot">
        <span>
          {t('metering.atLimit', {
            action: t(`metering.exhaustion.${quota.on_exhaustion}`),
          })}
        </span>
        <span className="lq-mt__standing" data-tone={tone}>
          {t(`metering.quotaTone.${tone}`)}
        </span>
      </div>
    </li>
  );
}

function costFor(summary: CostSummary | undefined, meter: Meter): string | null {
  if (summary === undefined) return null;
  return summary.lines.find((line) => line.meter === meter)?.cost ?? null;
}

export function UsageAndCost(): ReactElement {
  const { t } = useTranslation();
  const usage = useUsage();
  const cost = useCost();
  const quotas = useQuotas();

  const header = (
    <PageHeader title={t('metering.title')} lede={t('metering.description')} />
  );

  if (usage.isLoading) {
    return (
      <div className="lq-stack">
        {header}
        <SkeletonRows rows={4} />
      </div>
    );
  }

  if (usage.isError) {
    return (
      <div className="lq-stack">
        {header}
        <Alert tone="error" title={t('metering.loadFailed')}>
          {usage.error.message}
        </Alert>
      </div>
    );
  }

  const lines = usage.data ?? [];
  const summary = cost.data;
  const currency = summary?.currency ?? null;
  const configured = quotas.data ?? [];
  const consumedByMeter = usageByMeter(lines);

  return (
    <div className="lq-stack">
      {header}
      <Alert tone="notice" title={t('metering.lag.title')}>
        {t('metering.lag.body')}
      </Alert>
      {summary !== undefined && hasUnpricedUsage(summary) ? (
        <Alert tone="warning" title={t('metering.unpricedAlert.title')}>
          {t('metering.unpricedAlert.body', {
            meters: summary.unpriced_meters
              .map((meter) => t(meterLabelKey(meter)))
              .join(', '),
          })}
        </Alert>
      ) : null}

      {lines.length === 0 ? (
        <EmptyState
          title={t('metering.empty.title')}
          guidance={t('metering.empty.body')}
        />
      ) : (
        <Card flush>
          <div className="lq-mt__head">
            <h2 className="lq-mt__title">{t('metering.section.period')}</h2>
            {summary === undefined ? null : (
              <span className="lq-mt__window">
                {t('metering.window', {
                  start: new Date(summary.window_start).toLocaleDateString(),
                  end: new Date(summary.window_end).toLocaleDateString(),
                })}
              </span>
            )}
          </div>
          <DataTable
            caption={t('metering.usage.caption')}
            columns={[
              { key: 'meter', label: t('metering.column.meter') },
              { key: 'units', label: t('metering.column.units'), numeric: true },
              { key: 'cost', label: t('metering.column.cost'), numeric: true },
            ]}
          >
            {lines.map((line) => (
              <UsageRow
                key={line.meter}
                line={line}
                cost={costFor(summary, line.meter)}
                currency={currency}
              />
            ))}
          </DataTable>
          {summary !== undefined && summary.lines.length > 0 ? (
            <div className="lq-mt__total">
              {t('metering.total', { total: summary.total, currency: currency ?? '' })}
            </div>
          ) : null}
        </Card>
      )}

      {configured.length === 0 ? (
        <Alert tone="notice" title={t('metering.noQuota.title')}>
          {t('metering.noQuota.body')}
        </Alert>
      ) : (
        <Card flush>
          <div className="lq-mt__head">
            <h2 className="lq-mt__title">{t('metering.section.limits')}</h2>
            <span className="lq-mt__window">{t('metering.quotas.caption')}</span>
          </div>
          <ul className="lq-mt__limits">
            {configured.map((quota) => (
              <QuotaRow
                key={quota.meter}
                quota={quota}
                consumed={consumedByMeter.get(quota.meter) ?? 0}
              />
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
